import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Carregando } from './components/Sga';
import { api } from './lib/api';
import { useAuth } from './lib/auth';
import type {
  Conexao,
  Dashboard as DashboardDto,
  Fila,
  LogIntegracao,
  PainelAntiban,
  PerfilAntiban,
  SerieFila,
} from './lib/types';
import { useEventos } from './lib/ws';
import { MessengerMobile } from './mobile/MessengerMobile';
import { QrPairing, type ModoApresentacao } from './pairing/QrPairing';
import { ConexaoCadastro } from './screens/ConexaoCadastro';
import { Conexoes } from './screens/Conexoes';
import { Dashboard } from './screens/Dashboard';
import { Filas } from './screens/Filas';
import { Login } from './screens/Login';
import { RecuperarSenha } from './screens/RecuperarSenha';
import { Clientes } from './screens/Clientes';
import { Setores } from './screens/Setores';
import { MarcaTela } from './screens/MarcaTela';
import { Logs } from './screens/Logs';
import { Shell, type Tela } from './shell/Shell';
import { Toast, useToast } from './shell/Toast';

/** Recarrega os painéis periodicamente, como rede de segurança do WebSocket. */
const INTERVALO_POLLING_MS = 30_000;

interface Emparelhamento {
  modo: ModoApresentacao;
  conexao: Conexao | null;
}

export default function App() {
  const { carregando: carregandoSessao, usuario, tenantAtivo } = useAuth();
  const { aviso, avisar } = useToast();

  const [tela, setTela] = useState<Tela>('dashboard');
  const [vistaConexao, setVistaConexao] = useState<'lista' | 'cadastro'>('lista');
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [mobile, setMobile] = useState(
    () => window.matchMedia('(max-width: 900px)').matches,
  );
  const [emparelhamento, setEmparelhamento] = useState<Emparelhamento | null>(null);
  const [wsConectado, setWsConectado] = useState(false);

  // Token do link de recuperacao de senha (`#/redefinir-senha?token=...`).
  //
  // Lido e a URL e limpa em seguida: o token nao fica no historico do navegador
  // nem viaja no `Referer` de qualquer requisicao seguinte.
  const [tokenSenha, setTokenSenha] = useState<string | null>(tokenDoHash);

  // O clique no link do e-mail nem sempre carrega a pagina do zero: com uma aba
  // ja aberta neste dominio, o navegador reusa a aba e so troca o hash. Sem
  // este listener o endereco mudava e a tela ficava a mesma.
  useEffect(() => {
    const aoTrocarHash = () => {
      const t = tokenDoHash();
      if (t) setTokenSenha(t);
    };
    window.addEventListener('hashchange', aoTrocarHash);
    return () => window.removeEventListener('hashchange', aoTrocarHash);
  }, []);

  // Dados
  const [dashboard, setDashboard] = useState<DashboardDto | null>(null);
  const [conexoes, setConexoes] = useState<Conexao[]>([]);
  const [perfis, setPerfis] = useState<PerfilAntiban[]>([]);
  const [fila, setFila] = useState<Fila | null>(null);
  const [serie, setSerie] = useState<SerieFila | null>(null);
  const [antiban, setAntiban] = useState<PainelAntiban | null>(null);
  const [logsMobile, setLogsMobile] = useState<LogIntegracao[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);

  // QR e autenticação vindos do WebSocket, repassados ao fluxo de emparelhamento.
  const [qrDoWs, setQrDoWs] = useState<
    { conexao_id: string; qr: string; expira_em_s: number } | null
  >(null);
  const [autenticouViaWs, setAutenticouViaWs] = useState<string | null>(null);

  const autenticado = Boolean(usuario && tenantAtivo);
  const tenantSlug = tenantAtivo?.slug ?? null;

  const carregar = useCallback(async () => {
    if (!autenticado) return;
    setCarregando(true);
    try {
      const [d, c, p, f, s, a] = await Promise.all([
        api.get<DashboardDto>('/dashboard'),
        api.get<{ conexoes: Conexao[] }>('/conexoes'),
        api.get<{ perfis: PerfilAntiban[] }>('/antiban/perfis'),
        api.get<Fila>('/fila', { limite: 60 }),
        api.get<SerieFila>('/fila/serie', { horas: 12 }),
        api.get<PainelAntiban>('/antiban/painel'),
      ]);
      setDashboard(d);
      setConexoes(c.conexoes);
      setPerfis(p.perfis);
      setFila(f);
      setSerie(s);
      setAntiban(a);
      setErro(null);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [autenticado]);

  // Recarrega tudo quando entra e a cada troca de tenant.
  useEffect(() => {
    if (!autenticado) return;
    setSelecionada(null);
    setVistaConexao('lista');
    void carregar();
  }, [autenticado, tenantSlug, carregar]);

  // Polling de segurança: se o WebSocket cair, a tela não congela.
  useEffect(() => {
    if (!autenticado) return;
    const id = window.setInterval(() => {
      if (!document.hidden) void carregar();
    }, INTERVALO_POLLING_MS);
    return () => window.clearInterval(id);
  }, [autenticado, carregar]);

  // Logs para a aba mobile (no desktop a tela de Logs busca por conta própria).
  useEffect(() => {
    if (!autenticado || !mobile) return;
    api
      .get<{ registros: LogIntegracao[] }>('/logs', { limite: 60 })
      .then((r) => setLogsMobile(r.registros))
      .catch(() => setLogsMobile([]));
  }, [autenticado, mobile, tenantSlug]);

  // Recarga com debounce: uma rajada de eventos vira uma chamada.
  const recargaPendente = useRef<number | undefined>(undefined);
  const agendarRecarga = useCallback(() => {
    if (recargaPendente.current) window.clearTimeout(recargaPendente.current);
    recargaPendente.current = window.setTimeout(() => void carregar(), 800);
  }, [carregar]);

  useEventos(
    autenticado,
    useCallback(
      (evento) => {
        switch (evento.tipo) {
          case 'qr_atualizado':
            setQrDoWs({
              conexao_id: evento.conexao_id,
              qr: evento.qr,
              expira_em_s: evento.expira_em_s,
            });
            break;

          case 'sessao_atualizada':
            // Atualiza a linha na hora; o resto vem na recarga.
            setConexoes((atual) =>
              atual.map((c) =>
                c.id === evento.conexao_id
                  ? {
                      ...c,
                      status: evento.status,
                      numero: evento.numero ?? c.numero,
                      device_label: evento.device_label ?? c.device_label,
                    }
                  : c,
              ),
            );
            if (evento.estado_sessao === 'autenticada') {
              setAutenticouViaWs(evento.conexao_id);
              avisar('Sessão autenticada');
            }
            if (evento.status === 'banido') {
              avisar('Número banido pelo provedor — envios suspensos', true);
            }
            agendarRecarga();
            break;

          case 'fila_movimentou':
            setConexoes((atual) =>
              atual.map((c) =>
                c.id === evento.conexao_id ? { ...c, na_fila: evento.na_fila } : c,
              ),
            );
            agendarRecarga();
            break;

          case 'antiban_evento':
            avisar(`Anti-Ban: ${evento.detalhe}`);
            agendarRecarga();
            break;

          case 'mensagem_recebida':
            agendarRecarga();
            break;

          case 'container_saude':
            setDashboard((atual) =>
              atual
                ? {
                    ...atual,
                    containers: atual.containers.map((c) =>
                      c.servico === evento.servico
                        ? { ...c, status: evento.status, latencia_ms: evento.latencia_ms }
                        : c,
                    ),
                  }
                : atual,
            );
            break;

          case 'resync':
            // O painel ficou para trás; recarrega tudo por REST.
            void carregar();
            break;
        }
      },
      [agendarRecarga, avisar, carregar],
    ),
    setWsConectado,
  );

  const periodo = useMemo(() => {
    const agora = new Date();
    const inicio = new Date(agora);
    inicio.setHours(agora.getHours() - 12);
    const fmt = (d: Date) =>
      d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `Período: hoje — ${fmt(inicio)} às ${fmt(agora)}`;
  }, [dashboard?.atualizado_em]);

  const callbackCloud = useMemo(() => {
    const base = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080').replace(/\/+$/, '');
    return `${base}/hooks/cloud/${tenantSlug ?? 'tenant'}`;
  }, [tenantSlug]);

  const abrirEmparelhamento = useCallback(
    (conexaoId: string | null) => {
      setQrDoWs(null);
      setAutenticouViaWs(null);
      setEmparelhamento({
        // Desktop abre em modal; no aparelho um card de 820px nao cabe, entao
        // vai em tela cheia.
        modo: mobile ? 'cheia' : 'modal',
        conexao: conexaoId ? (conexoes.find((c) => c.id === conexaoId) ?? null) : null,
      });
    },
    [conexoes, mobile],
  );

  const excluirConexao = useCallback(
    async (c: Conexao) => {
      // Ação destrutiva: confirma pelo nome para não errar a linha.
      if (
        !window.confirm(
          `Excluir a conexão "${c.nome}"?\n\nA sessão é encerrada no provedor e a conexão sai do painel. ` +
            `O histórico de mensagens e de logs continua auditável.`,
        )
      ) {
        return;
      }
      try {
        await api.del(`/conexoes/${c.id}`);
        avisar(`Conexão "${c.nome}" excluída`);
        setSelecionada(null);
        await carregar();
      } catch (e) {
        avisar((e as Error).message, true);
      }
    },
    [avisar, carregar],
  );

  if (carregandoSessao) {
    return (
      <div className="sga-login">
        <Carregando texto="Abrindo o painel…" />
      </div>
    );
  }

  // Antes do check de sessao: quem clica no link do e-mail precisa chegar na
  // tela mesmo com uma sessao velha ainda guardada no navegador.
  if (tokenSenha) {
    return <RecuperarSenha token={tokenSenha} aoVoltar={() => setTokenSenha(null)} />;
  }

  if (!autenticado) return <Login />;

  const conteudo = () => {
    if (tela === 'dashboard') {
      return (
        <Dashboard
          dados={dashboard}
          carregando={carregando}
          erro={erro}
          aoRecarregar={carregar}
          tenantNome={tenantAtivo?.nome ?? ''}
          aoVerLogs={() => setTela('logs')}
        />
      );
    }

    if (tela === 'conexoes') {
      if (vistaConexao === 'cadastro' && selecionada) {
        return (
          <ConexaoCadastro
            conexaoId={selecionada}
            perfis={perfis}
            podeEscrever={usuario?.pode_escrever ?? false}
            aoVoltar={() => setVistaConexao('lista')}
            aoSalvo={async (nome) => {
              avisar(`Conexão "${nome}" salva`);
              await carregar();
            }}
            aoReemparelhar={abrirEmparelhamento}
            aoVerLogs={() => setTela('logs')}
          />
        );
      }

      return (
        <Conexoes
          conexoes={conexoes}
          carregando={carregando}
          erro={erro}
          aoRecarregar={carregar}
          tenantNome={tenantAtivo?.nome ?? ''}
          busca={busca}
          aoBuscar={setBusca}
          selecionada={selecionada}
          aoSelecionar={setSelecionada}
          aoEditar={(id) => {
            setSelecionada(id);
            setVistaConexao('cadastro');
          }}
          aoReemparelhar={abrirEmparelhamento}
          aoExcluir={excluirConexao}
          aoNova={() => abrirEmparelhamento(null)}
          podeEscrever={usuario?.pode_escrever ?? false}
        />
      );
    }

    if (tela === 'setores') {
      return <Setores periodo={periodo} podeEscrever={usuario?.pode_escrever ?? false} />;
    }
    if (tela === 'clientes') {
      return <Clientes periodo={periodo} podeEscrever={usuario?.pode_escrever ?? false} />;
    }

    if (tela === 'marca') {
      return <MarcaTela periodo={periodo} podeEscrever={usuario?.pode_escrever ?? false} />;
    }

    if (tela === 'filas') {
      return (
        <Filas fila={fila} serie={serie} antiban={antiban} aoRecarregar={carregar} periodo={periodo} />
      );
    }

    return <Logs periodo={periodo} conexaoId={null} />;
  };

  if (mobile) {
    return (
      <>
        <MessengerMobile
          dashboard={dashboard}
          conexoes={conexoes}
          fila={fila}
          serie={serie}
          antiban={antiban}
          logs={logsMobile}
          aoVoltarDesktop={() => setMobile(false)}
          aoAbrirConexao={(id) => {
            setSelecionada(id);
            setVistaConexao('cadastro');
            setTela('conexoes');
            setMobile(false);
          }}
          aoNovaConexao={() => abrirEmparelhamento(null)}
        />
        {emparelhamento && (
          <QrPairing
            modo="cheia"
            conexao={emparelhamento.conexao}
            perfis={perfis}
            callbackCloud={callbackCloud}
            qrDoWs={qrDoWs}
            autenticouViaWs={autenticouViaWs}
            aoFechar={() => setEmparelhamento(null)}
            aoConcluir={async (nome) => {
              setEmparelhamento(null);
              avisar(`Conexão "${nome}" criada e ativa na fila`);
              await carregar();
            }}
          />
        )}
        <Toast aviso={aviso} mobile />
      </>
    );
  }

  return (
    <>
      <Shell
        tela={tela}
        aoNavegar={(t) => {
          setTela(t);
          if (t === 'conexoes') setVistaConexao('lista');
        }}
        containers={dashboard?.containers ?? []}
        totalConexoes={conexoes.length}
        totalFila={fila?.resumo.aguardando ?? 0}
        busca={busca}
        aoBuscar={(v) => {
          setBusca(v);
          if (v) setTela('conexoes');
        }}
        incidentes={dashboard?.incidentes.length ?? 0}
        wsConectado={wsConectado}
        aoVerNoCelular={() => setMobile(true)}
      >
        {conteudo()}
      </Shell>

      {emparelhamento && (
        <QrPairing
          modo={emparelhamento.modo}
          conexao={emparelhamento.conexao}
          perfis={perfis}
          callbackCloud={callbackCloud}
          qrDoWs={qrDoWs}
          autenticouViaWs={autenticouViaWs}
          aoFechar={() => setEmparelhamento(null)}
          aoConcluir={async (nome) => {
            setEmparelhamento(null);
            avisar(`Conexão "${nome}" criada e ativa na fila`);
            await carregar();
          }}
        />
      )}

      <Toast aviso={aviso} />
    </>
  );
}

/** Le o token de `#/redefinir-senha?token=...` e limpa a URL. */
function tokenDoHash(): string | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#/redefinir-senha')) return null;
  const t = new URLSearchParams(hash.slice(hash.indexOf('?') + 1)).get('token');
  window.history.replaceState(null, '', window.location.pathname);
  return t;
}
